import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { includes } from 'lodash'
import classnames from 'classnames/bind'
import { CheckboxField, Table } from '@adc/ui-adc'
import { formatDate, formatAuctionDateTime } from '../../utils/date'
import LinkExpandable from '../linkExpandable'
import VenueDetails from './venueDetails'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const { Row, Cell, Header, HeaderCell } = Table

export const VenueCheckbox = ({
  venue,
  selected = false,
  onChange,
  index,
  elmId,
}) => {
  return (
    <CheckboxField
      elmId={`${elmId}_venue_${venue.venue_id}_${index}_checkbox`}
      checked={selected}
      onChange={() => onChange(venue.venue_id)}
      className={cx('venue-checkbox-no-padding')}
      label={venue.venue_name}
    />
  )
}

VenueCheckbox.propTypes = {
  venue: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  elmId: PropTypes.string.isRequired,
}

const VenuesTable = ({
  elmId,
  className,
  venues,
  isOnline,
  onChangeVenues,
  openDetails = {},
  onToggleDetails,
  selected = [],
  filteredDate,
}) => {
  const isSelectedVenue = venueId => includes(selected, venueId)

  const toggleVenue = venueId => {
    const venuesSet = new Set(selected)
    venuesSet.has(venueId) ? venuesSet.delete(venueId) : venuesSet.add(venueId)
    onChangeVenues(Array.from(venuesSet))
  }

  const renderLiveRows = (id, venueList) =>
    venueList.map((venue, index) => {
      const detailsLink = (
        <LinkExpandable
          elmId={`${id}_venue_${venue.venue_id}_${index}_details_expandable`}
          expanded={openDetails[venue.venue_id]}
          toggleExpand={() => onToggleDetails(venue.venue_id)}
          title='Venue Details'
        />
      )
      return [
        <Row key={venue.venue_id}>
          <Cell>
            <VenueCheckbox
              venue={venue}
              selected={isSelectedVenue(venue.venue_id)}
              onChange={toggleVenue}
              index={index}
              elmId={id}
            />
          </Cell>
          <Cell
            data-elm-id={`${id}_venue_${venue.venue_id}_${index}_auction_date`}
          >
            {formatDate(
              venue.venue_schedule.bid_start_local_date,
              'MMM DD, YYYY'
            )}
          </Cell>
          <Cell data-elm-id={`${id}_venue_${venue.venue_id}_${index}_details`}>
            {detailsLink}
          </Cell>
        </Row>,
        openDetails[venue.venue_id] && (
          <Row key={venue.venue_id + '-details'}>
            <Cell colSpan='3'>
              <VenueDetails
                elmId={`${id}_venue_${venue.venue_id}_${index}`}
                venue={venue}
              />
            </Cell>
          </Row>
        ),
      ]
    })

  const renderOnlineRows = (id, venueList) =>
    venueList.map((venue, index) => (
      <Row key={venue.venue_id}>
        <Cell>
          <VenueCheckbox
            venue={venue}
            selected={isSelectedVenue(venue.venue_id)}
            onChange={toggleVenue}
            index={index}
            elmId={id}
          />
        </Cell>
        <Cell
          data-elm-id={`${id}_venue_${venue.venue_id}_${index}_auction_start`}
        >
          {formatAuctionDateTime(
            moment(
              `${venue.venue_schedule.bid_start_local_date} ${venue.venue_schedule.bid_start_local_time}`
            ),
            moment(
              `${venue.venue_schedule.initial_bidding_end_local_date} ${venue.venue_schedule.initial_bidding_end_local_time}`
            ),
            true
          )}
        </Cell>
        <Cell
          data-elm-id={`${id}_venue_${venue.venue_id}_${index}_auction_end`}
        >
          {formatAuctionDateTime(
            moment(
              `${venue.venue_schedule.bid_start_local_date} ${venue.venue_schedule.bid_start_local_time}`
            ),
            moment(
              `${venue.venue_schedule.initial_bidding_end_local_date} ${venue.venue_schedule.initial_bidding_end_local_time}`
            ),
            false
          )}
        </Cell>
      </Row>
    ))

  if (!venues || venues.length === 0) {
    return null
  }

  const filteredVenues = filteredDate
    ? venues.filter(
        venue => venue.venue_schedule.bid_start_local_date === filteredDate
      )
    : venues

  const venueRows = isOnline
    ? renderOnlineRows(elmId, filteredVenues)
    : renderLiveRows(elmId, filteredVenues)

  return (
    <div className={className}>
      <Table>
        <Header>
          <Row>
            <HeaderCell data-elm-id={`${elmId}_venue_header1`}>
              Location
            </HeaderCell>
            <HeaderCell data-elm-id={`${elmId}_venue_header2`}>
              {isOnline ? 'Auction Starts' : 'Auction Date'}
            </HeaderCell>
            <HeaderCell
              data-elm-id={`${elmId}_venue_header3`}
              className={cx({
                'venue-link': !isOnline,
              })}
            >
              {isOnline ? 'Auction Ends' : 'Auction Venue'}
            </HeaderCell>
          </Row>
        </Header>
        {venueRows}
      </Table>
    </div>
  )
}

VenuesTable.propTypes = {
  elmId: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  venues: PropTypes.array.isRequired,
  isOnline: PropTypes.bool.isRequired,
  onChangeVenues: PropTypes.func.isRequired,
  openDetails: PropTypes.object,
  onToggleDetails: PropTypes.func.isRequired,
  selected: PropTypes.array,
  filteredDate: PropTypes.string,
}

export default VenuesTable
